import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminAttachmentTemplateDetails } from '@/api/(admin)/attachment-templates';
import type { AdminAttachmentTemplateDetail } from '@/api/(admin)/attachment-templates/types';
import { adminAttachmentTemplatesQueryKey } from './useAdminAttachmentTemplates';

/**
 * Normalises one template detail.
 *
 * `rules`, `backupRule.documents` and `countryCodes` are all declared
 * non-optional by the response schema, but every consumer here iterates them,
 * so a defensive default turns a malformed payload into an empty section rather
 * than a blank page. `documentClass` is passed through untouched, including
 * `null` — see the note on `AdminAttachmentTemplateDocument`.
 */
function normalizeDetail(
  detail: AdminAttachmentTemplateDetail,
): AdminAttachmentTemplateDetail {
  const normalizeRule = (
    rule: AdminAttachmentTemplateDetail['rules'][number],
  ) => ({
    ...rule,
    facilityProcesses: rule.facilityProcesses ?? [],
    documents: (rule.documents ?? []).map(doc => ({
      ...doc,
      documentClass: doc.documentClass ?? null,
      countryCodes: doc.countryCodes ?? [],
    })),
  });

  return {
    ...detail,
    rules: (detail.rules ?? []).map(normalizeRule),
    backupRule: detail.backupRule ? normalizeRule(detail.backupRule) : null,
    hasOrders: detail.hasOrders ?? false,
  };
}

export function adminAttachmentTemplateDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...adminAttachmentTemplatesQueryKey, 'detail', id] as const,
    queryFn: () =>
      fetchAdminAttachmentTemplateDetails(id).then(res =>
        normalizeDetail(res.data),
      ),
  });
}

export function useAdminAttachmentTemplateDetail(id: string) {
  return useQuery({
    ...adminAttachmentTemplateDetailQueryOptions(id),
    enabled: Boolean(id),
  });
}
